import { useEffect, useState } from 'react'
import { useHistory } from "react-router"
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom';

import storage from "../../../util/storage"
import GetDetailAndStoreItUseCase from '../../../../useCase/Folder/GetDetailAndStoreItUseCase'
import FetchFolderGateway from '../../../../gateway/Folder/FetchFolderGateway'
import LocalStorageGateway from '../../../../gateway/Form/LocalStorageGateway'
import useModal from "../../modal/useModal"
import PageLoader from "../../pageLoader/PageLoader"
import envVariable from "../../../util/envVariable"
import FolderModal from './FolderModal'
import FolderDetails from './FolderDetails'
import { Props } from './types'
import { ErrorResponse } from '../../../../domain/Api/Type'
import ErrorModal from '../../modal/ErrorModal'
import { EModule } from '../../../../domain/User/Emodule'
import { useToast } from '../../toast/ToastContext';

const FolderComponent = ({ handleDeleteFolder, FolderDetail, type, forms, state }: Props): JSX.Element => {
  const history = useHistory()
  const { t } = useTranslation()
  const { addToast } = useToast();

  const role = storage.getRole()
  const userProfile = storage.getUserInformation()
  const [isShowing, toggle] = useModal()
  const [isShowingDuplicate, toggleDuplicate] = useModal()
  const [isShowingModifyFolderWaitingSign, toggleModifyFolderWaitingSign] = useModal()
  const [isShowingForceValidate, toggleForceValidate] = useModal()
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isLoadingDuplicate, setLoadingDuplicate] = useState<boolean>(false)
  const [isLoadingModifyFolderWaitingSign, setLoadingModifyFolderWaitingSign] = useState<boolean>(false)
  const [showDocusignHistory, setShowDocusignHistory] = useState<boolean>(false)
  const allowDownloadDocument = (envVariable('REACT_APP_ALLOW_DOWNLOAD_DOCUMENT') === 'true')
  const [showEnvelopeHistoryModal, toggleEnvelopeHistory] = useModal()
  const [contentModal, setContentModal] = useState<JSX.Element | null>(null)
  const [modalSize, setModalSize] = useState<string>('modal')
  const [errorMessage, setErrorMessage] = useState<string|null>(null)
  const location = useLocation();

  useEffect(() => {
    if (error) {
        setTimeout(() => setError(false), 5000)
    }
  }, [error])

  const handleEdit = () => {
    storage.setFolderId(FolderDetail.id)
    storage.setFormId(FolderDetail.formId, FolderDetail.id)
    storage.setDossierState(FolderDetail.status, FolderDetail.id)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(FolderDetail.id))
    const isCorrection = location.pathname.includes('/correction');

    if (isCorrection) {
      localStorage.setItem('correction_mode', 'true')
    } else {
      localStorage.setItem('correction_mode', 'false')
    }

    getDetailAndStoreItUseCase.execute(FolderDetail.id).then((response) => {
      if (response !== null) {
        history.push(response.url)
      }
    })
  }

  const handleDelete = () => {
    new FetchFolderGateway().updateFolderStatusToCanceled(FolderDetail.id)
      .then(() => {
        handleDeleteFolder(FolderDetail.id)
        setError(false)
      })
      .catch((e) => {
        console.error(e)
        setError(true)
      })
  }

  const handleDownload = () => {
    setLoading(true)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(FolderDetail.id))

    getDetailAndStoreItUseCase?.downloadSignedDocument(FolderDetail.id)?.then((response) => {
      if (response !== null) {
        const url = URL.createObjectURL(response)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = `dossier-${String(FolderDetail.id)}.zip`
        a.click()
        window.URL.revokeObjectURL(url)
      }
    })
      .catch((errors: ErrorResponse) => {
        errors.errors.forEach(error => {
          setErrorMessage(error.message)
        });
        setErrorMessage(t('docusign.error-download-signed-documents'))
      })
      .finally(() => setLoading(false))
  }

  const handleSignatureProof = () => {
    setLoading(true)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(FolderDetail.id))

    getDetailAndStoreItUseCase?.downloadSignatureProof(FolderDetail.id)?.then((response) => {
      if (response !== null) {
        const url = URL.createObjectURL(response)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = `preuve-signature-${String(FolderDetail.id)}.pdf`
        a.click()
        window.URL.revokeObjectURL(url)
      }
    })
      .catch((errors: ErrorResponse) => {
        errors.errors.forEach(error => {
          setErrorMessage(error.message)
        });
        setErrorMessage(t('docusign.error-content'))
      })
      .finally(() => setLoading(false))
  }

  const handleDocuSignHistory = () => {
    setShowDocusignHistory(true)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(FolderDetail.id))

    getDetailAndStoreItUseCase?.getFolderDocuSignHistory(FolderDetail.id)?.then((blob: Blob) => {
      setContentModal(buildModalContent(blob))
      setShowDocusignHistory(false)
    })
      .catch((error) => {
        setShowDocusignHistory(false)
        if (error?.response?.data?.detail?.includes('ENVELOPE_DOES_NOT_EXIST')) {
          setErrorMessage(t('docusign.error-folder-not-found'))
        } else {
          setErrorMessage(t('docusign.error-content'))
        }
      })
  }

  const handleManageDossier = () => {
    storage.setFolderId(FolderDetail.id)
    storage.setFormId(FolderDetail.formId, FolderDetail.id)
    if (userProfile?.modules?.includes(EModule.PlatformValidator)) {
      history.push(`/plateforme-dossier/${FolderDetail.id}`)
    } else if (userProfile?.modules?.includes(EModule.PreSignatureValidator)) {
      history.push(`/pre-signature-validation/${FolderDetail.id}`)
    } else {
      history.push(`/manage-dossier/${FolderDetail.id}`)
    }
  }

  const buildModalContent = (blob: Blob) => {
    setModalSize('modal-xl')
    toggleEnvelopeHistory()

    if (blob) {
      const resource = window.URL.createObjectURL(blob)

      return (
        <div className="pj-modal-content-center">
          <iframe src={resource} width="100%" style={{ height: '80vh' }} />
        </div>
      )
    }

    return (
      <div className="pj-modal-content-center">
        <p className="notification notification--error">{t('docusign.error-folder-not-found')}</p>
      </div>
    )
  }

  const handleDuplicate = (documentDuplicate) => {
    setLoadingDuplicate(true)
    new FetchFolderGateway().duplicateFolder(FolderDetail.id, documentDuplicate)
      .then((folder) => {
        if (folder) {
          storage.setFolderId(folder.id)
          storage.setFormId(folder.form_id, folder.id)
          const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(folder.id))

          getDetailAndStoreItUseCase.execute(folder.id).then((response) => {
            if (response !== null) {
              history.push(response.url)
            }
          })
        }
        setLoadingDuplicate(false)
        setError(false)
      })
      .catch((e) => {
        console.error(e)
        setLoadingDuplicate(false)
        setError(true)
      })
  }

  const handleForceValidate = () => {
    new FetchFolderGateway().forceValidateFolder(FolderDetail.id)
      .then(() => {
        FolderDetail.plateforme_state = 'validated'
        toggleForceValidate()
        setError(false)
      })
      .catch((e) => {
        toggleForceValidate()
        console.error(e)
        setError(true)
      })
  }

  const handleModifyFolderWaitingSign = () => {
    setLoadingModifyFolderWaitingSign(true)
    toggleModifyFolderWaitingSign()
    new FetchFolderGateway().modifyFolderWaitingSign(FolderDetail.id)
      .then(() => {
        addToast('success', t('folder.modal-modify-waiting-signature.success'))
        handleDeleteFolder(FolderDetail.id)
        setError(false)
      })
      .catch((e) => {
        addToast('error', t('folder.modal-modify-waiting-signature.error'))
        console.error(e)
        setError(true)
      })
      .finally(() => {
        setLoadingModifyFolderWaitingSign(false)
      })
  }

  const label = forms.find(form => form.id === FolderDetail.formId)?.label;
  const FolderStatuses = FolderDetail.status.split('|').map(status => [t(`folder.state.folder`), t(`${label}`).toLowerCase(), t(`folder.state.${status}-state`)].join(' '))

  const getInvestorTypeClass = (type: boolean|null): string => {
    return type === false ? 'type-pm' : 'type-pp';
  };

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      
      {/* Modals */}
      {showEnvelopeHistoryModal && (
        <FolderModal title={t('docusign.modal-preview-title')} description='' customClassName={modalSize} hide={toggleEnvelopeHistory}>
          {contentModal}
        </FolderModal>
      )}
      {isShowing && <FolderModal title={t('folder.modal-cancellation.title')}
          description={t('folder.modal-cancellation.description')}
          hide={toggle}
          customClassName={`modal`}
          forceClose={false}>
          <div className="modal-container-buttons">
            <button type="button"
              className="button-reset button-primary--outline button--medium button--width-fit"
              onClick={toggle}
            >
              {t('folder.modal-cancellation.no-i-decline')}
            </button>
            <button type="button"
              className="button-reset button-error--outline button--medium button--width-fit"
              onClick={handleDelete}
            >
              {t('folder.modal-cancellation.yes-i-delete-subscription-folder')}
            </button>
          </div>
        </FolderModal>}
        {isShowingDuplicate && <FolderModal title={t('folder.modal-duplication.title')}
          description={t('folder.modal-duplication.description')}
          hide={toggleDuplicate}
          customClassName={`modal`}
          forceClose={false}>
          <div className="modal-container-buttons">
            <button type="button"
              className="button-reset button-primary--outline button--medium button--width-fit"
              onClick={() => handleDuplicate(false)}
            >
              {t('folder.modal-duplication.no-i-decline')}
            </button>
            <button type="button"
              className="button-reset button-primary--outline button--medium button--width-fit"
              onClick={() => handleDuplicate(true)}
            >
              {t('folder.modal-duplication.yes-i-duplicate-documents')}
            </button>
          </div>
        </FolderModal>}
        {isShowingForceValidate &&
          <FolderModal title={t('folder.modal-force-validation.title')}
            description={t('folder.modal-force-validation.description')}
            hide={toggleForceValidate}
            customClassName={`modal`}
            forceClose={false}>
            <div className="modal-container-buttons">
              <button type="button"
                className="button-reset button-primary--outline button--medium button--width-fit"
                onClick={toggleForceValidate}
              >
                {t('folder.modal-force-validation.no-i-decline')}
              </button>
              <button type="button"
                className="button-reset button-primary--outline button--medium button--width-fit"
                onClick={handleForceValidate}
              >
                {t('folder.modal-force-validation.yes-i-force-validate')}
              </button>
            </div>
          </FolderModal>
        }
        {isShowingModifyFolderWaitingSign && <FolderModal title={t('folder.modal-modify-waiting-signature.title')}
          description={t('folder.modal-modify-waiting-signature.description')}
          hide={toggleModifyFolderWaitingSign}
          customClassName={`modal`}
          forceClose={false}>
          <div className="modal-container-buttons">
            <button type="button"
              className="button-reset button-primary--outline button--medium button--width-fit"
              onClick={toggleModifyFolderWaitingSign}
            >
              {t('folder.modal-modify-waiting-signature.no-i-decline')}
            </button>
            <button type="button"
              className="button-reset button-primary--outline button--medium button--width-fit"
              onClick={handleModifyFolderWaitingSign}
            >
              {t('folder.modal-modify-waiting-signature.yes-i-modify')}
            </button>
          </div>
        </FolderModal>}

      {isLoadingModifyFolderWaitingSign && <PageLoader description={t('folder.modal-modify-waiting-signature.display-modify-loader')} />}

      <tr className="folder-row">
        {isLoading && <td colSpan={10}><PageLoader /></td>}
        {showDocusignHistory && <td colSpan={10}><PageLoader description={t('docusign.display-history-loader')} /></td>}
        {isLoadingDuplicate && <td colSpan={10}><PageLoader description={t('folder.modal-duplication.display-duplicate-loader')} /></td>}
        
        {!isLoading && !showDocusignHistory && !isLoadingDuplicate && (
          <FolderDetails
            FolderDetail={FolderDetail} 
            error={error} 
            FolderStatuses={FolderStatuses}
            getInvestorTypeClass={getInvestorTypeClass}
            actions={{
                role,
                handleEdit,
                handleDownload,
                handleDocuSignHistory,
                handleSignatureProof,
                handleManageDossier,
                setIsPopoverOpen,
                isPopoverOpen,
                toggle,
                toggleDuplicate,
                toggleForceValidate,
                toggleModifyFolderWaitingSign,
                allowDownloadDocument,
                setError,
                type,
                state
            }}
          />
        )}
      </tr>
    </>
  )
}

export default FolderComponent